<template>
  <v-container py-0>
    <v-card color="transparent" flat light>
      <v-card-text class="py-0">
        <v-layout wrap row>
          <v-flex md6 xs12 px-2>
            <date-picker
              v-model="localMatch.date"
              v-validate="'required'"
              :label="$t('fields.global.date') + ' (*)'"
              :data-vv-name="$tc('fields.global.date')"
              :error-messages="errors.collect($tc('fields.global.date'))"
              :min="tournament ? formatDatePicker(tournament.startAt) : null"
              :max="tournament ? formatDatePicker(tournament.endAt) : null"
              :readonly="localMatch.started"
            />
          </v-flex>
          <v-flex v-show="!phaseHours" md6 xs12 px-2>
            <v-menu
              ref="menuTime"
              v-model="menuTime"
              :close-on-content-click="false"
              :nudge-right="40"
              lazy
              transition="scale-transition"
              offset-y
              full-width
              max-width="290px"
              min-width="290px"
            >
              <template #activator="{ on }">
                <v-text-field
                  v-model="localMatch.time"
                  v-validate="{ required: !phaseHours }"
                  prepend-icon="access_time"
                  readonly
                  :label="$tc('fields.global.time') + ' (*)'"
                  :data-vv-name="$tc('fields.global.time')"
                  :error-messages="errors.collect($tc('fields.global.time'))"
                  v-on="on"
                />
              </template>
              <v-time-picker
                v-if="menuTime"
                v-model="localMatch.time"
                full-width
                :readonly="localMatch.started"
                @change="onChangeTime"
                @click:minute="$refs.menuTime.save(localMatch.time)"
              />
            </v-menu>
          </v-flex>
          <v-flex v-show="!!phaseHours" md6 xs12 px-2>
            <v-select
              v-model="localMatch.time"
              v-validate="{ required: !!phaseHours }"
              :items="phaseHours"
              prepend-icon="access_time"
              :label="$tc('fields.global.time') + ' (*)'"
              :data-vv-name="$t('fields.global.time')"
              :error-messages="errors.collect($t('fields.global.time'))"
              @change="onChangeTime"
            />
          </v-flex>
          <v-flex md4 xs12 px-2>
            <v-select
              v-model="localMatch.type"
              v-validate="'required'"
              :items="_matchTypes"
              :readonly="!!localMatch._id || !!tournament"
              :label="
                $t('action.actions.select', {
                  field: $t('fields.global.type'),
                }) + ' (*)'
              "
              :data-vv-name="$t('fields.global.type')"
              :error-messages="errors.collect($t('fields.global.type'))"
              @change="onChangeType"
            />
          </v-flex>
          <v-flex v-if="localMatch.type === 'friendly'" md4 xs12 px-2>
            <v-text-field
              v-model="localMatch.name"
              :label="$t('titles.global.nameMatch')"
              :data-vv-name="$t('titles.global.nameMatch')"
              :error-messages="errors.collect($t('titles.global.nameMatch'))"
            />
            <v-list v-if="localMatch.type === 'friendly'">
              <v-list-item-group>
                <v-list-item
                  v-for="name in names"
                  :key="name._id"
                  class="cursor"
                >
                  <v-chip outline link color="primary" @click="setName(name)">
                    {{ name.name }}
                  </v-chip>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-flex>
          <v-flex v-if="localMatch.type === 'tournament'" md5 xs12 px-2>
            <tournament-field
              v-model="localMatch.tournamentId"
              v-validate="{ required: localMatch.type === 'tournament' }"
              :readonly="!!tournament"
              :clearable="!tournament"
              :label="
                $t('action.actions.select', {
                  field: $tc('models.tournament'),
                }) + ' (*)'
              "
              :data-vv-name="$tc('models.tournament')"
              :error-messages="errors.collect($tc('models.tournament'))"
            />
          </v-flex>
          <v-flex v-if="localMatch.type === 'league'" md3 xs12 px-2>
            <v-text-field
              v-model="localMatch.session"
              v-validate="{ numeric: !!localMatch.categoryId }"
              type="number"
              :label="$tc('fields.matches.session')"
              :data-vv-name="$tc('fields.matches.session')"
              :error-messages="errors.collect($tc('fields.matches.session'))"
            />
          </v-flex>

          <!-- Home team -->

          <v-flex md12 xs12>
            <v-list>
              <v-label> {{ $tc('fields.matches.homeTeam') }} </v-label>
              <v-divider />
            </v-list>
          </v-flex>

          <v-flex xs12>
            <v-layout wrap row>
              <v-flex md4 xs12>
                <entity-field
                  v-model="localMatch.homeEntityId"
                  :tournament-id="localMatch.tournamentId"
                  :label="$tc('models.entity')"
                  :disabled="localMatch.type === ''"
                  :filter-data="true"
                />
              </v-flex>
              <v-flex md8 xs12 px-2>
                <team-field
                  key="homeTeam"
                  v-model="localMatch.homeTeamId"
                  v-validate="'required'"
                  :category-id="localMatch.categoryId"
                  :entity-id="localMatch.homeEntityId"
                  :tournament-id="localMatch.tournamentId"
                  :disabled="localMatch.type === ''"
                  :label="
                    $t('action.actions.select', {
                      field: $t('fields.matches.homeTeam'),
                    }) + ' (*)'
                  "
                  :data-vv-name="$tc('fields.matches.homeTeam')"
                  :error-messages="
                    errors.collect($tc('fields.matches.homeTeam'))
                  "
                  :readonly="localMatch.started || localMatch.streamingStarted"
                  :allow-create="true"
                  @change="$emit('changeHomeTeam', localMatch.homeTeamId)"
                  @openModal="openNewTeamModal('home')"
                />
              </v-flex>

              <v-flex md9 xs12 px-2>
                <category-menu
                  v-model="localMatch.categoryId"
                  v-validate="{
                    required:
                      localMatch.type !== 'tournament' &&
                      localMatch.type === 'league',
                  }"
                  :disabled="localMatch.type === ''"
                  clearable
                  :label="
                    $t('action.actions.select', {
                      field: $tc('models.category'),
                    })
                  "
                  :data-vv-name="$tc('models.category')"
                  :error-messages="errors.collect($tc('models.category'))"
                  @duration="(duration) => (matchesDuration = duration)"
                />
              </v-flex>

              <v-flex md2 xs12 px-2>
                <v-text-field
                  v-model.number="match.finalScoreHomeTeam"
                  v-validate="'numeric'"
                  :disabled="localMatch.type === ''"
                  type="number"
                  :label="$t('titles.matches.score')"
                  :data-vv-name="$tc('titles.matches.homeTeamResult')"
                  :error-messages="
                    errors.collect($tc('titles.matches.homeTeamResult'))
                  "
                />
              </v-flex>

              <!-- Visitor team -->
              <v-flex md12 xs12>
                <v-list>
                  <v-label> {{ $tc('fields.matches.visitorTeam') }} </v-label>
                  <v-divider />
                </v-list>
              </v-flex>
              <v-flex md4 xs12>
                <entity-field
                  v-model="localMatch.visitorEntityId"
                  :tournament-id="localMatch.tournamentId"
                  :label="$tc('models.entity')"
                  :disabled="localMatch.homeTeamId === ''"
                />
              </v-flex>

              <v-flex md8 xs12 px-2>
                <team-field
                  key="visitorTeam"
                  v-model="localMatch.visitorTeamId"
                  v-validate="'required'"
                  all
                  :tournament-id="localMatch.tournamentId"
                  :entity-id="localMatch.visitorEntityId"
                  :category-id="
                    localMatch.type === 'league' ? auxHomeCategoryId : ''
                  "
                  :disabled="localMatch.homeTeamId === ''"
                  :label="
                    $t('action.actions.select', {
                      field: $t('fields.matches.visitorTeam'),
                    }) + ' (*)'
                  "
                  :data-vv-name="$tc('fields.matches.visitorTeam')"
                  :error-messages="
                    errors.collect($tc('fields.matches.visitorTeam'))
                  "
                  :readonly="localMatch.started || localMatch.streamingStarted"
                  :allow-create="true"
                  @change="$emit('changeVisitorTeam', localMatch.visitorTeamId)"
                  @openModal="openNewTeamModal('visitor')"
                />
              </v-flex>
              <v-flex md9 xs12 px-2>
                <category-menu
                  v-model="localMatch.categoryIdVisitor"
                  v-validate="{
                    required:
                      localMatch.type !== 'tournament' &&
                      localMatch.type === 'league',
                  }"
                  :disabled="
                    localMatch.type !== 'friendly' ||
                    localMatch.homeTeamId === ''
                  "
                  clearable
                  :label="
                    $t('action.actions.select', {
                      field: $tc('models.category'),
                    })
                  "
                  :data-vv-name="$tc('models.category')"
                  :error-messages="errors.collect($tc('models.category'))"
                />
              </v-flex>

              <v-flex md2 xs12 px-2>
                <v-text-field
                  v-model.number="match.finalScoreVisitTeam"
                  v-validate="'numeric'"
                  type="number"
                  :disabled="localMatch.homeTeamId === ''"
                  :label="$t('titles.matches.score')"
                  :data-vv-name="$tc('titles.matches.visitorTeamResult')"
                  :error-messages="
                    errors.collect($tc('titles.matches.visitorTeamResult'))
                  "
                />
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex xs12>
            <v-layout row wrap>
              <v-flex md6 xs12 px-2>
                <pitch-field
                  v-model="localMatch.location"
                  v-validate="{
                    required:
                      !!localMatch.recording ||
                      !!localMatch.streamingAndRecording,
                  }"
                  :_id="group && group.pitch ? group.pitch : ''"
                  :team-id="localMatch.homeTeamId"
                  :tournament-id="localMatch.tournamentId"
                  :readonly="!!group && !!group.pitch && !!group.pitch.length"
                  :clearable="
                    (!group || !group.pitch || !group.pitch.length) &&
                    !localMatch.streamingStarted
                  "
                  :label="
                    $t('action.actions.select', {
                      field: $t('fields.global.location'),
                    })
                  "
                  :error-messages="
                    errors.collect($tc('fields.global.location'))
                  "
                  :data-vv-name="$tc('fields.global.location')"
                />
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex md3 xs6 px-2>
            <v-radio-group
              v-model="streamingType"
              :readonly="localMatch.streamingStarted"
            >
              <v-radio
                :label="$t('titles.matches.noRecord')"
                color="primary"
                value="noRecord"
              />
              <v-radio
                :label="$t('fields.matches.recording')"
                color="primary"
                value="recording"
              />
              <v-radio
                :label="$t('fields.matches.streamingAndRecording')"
                color="primary"
                value="streamingAndRecording"
              />
            </v-radio-group>
          </v-flex>
          <template>
            <v-flex md3 xs6 px-2>
              <v-switch
                v-model="localMatch.autoStreaming"
                :readonly="localMatch.streamingStarted"
                :label="$t('fields.matches.autoStreaming')"
                color="primary"
                @change="onChangeAutoStreaming"
              />
            </v-flex>
            <v-flex v-if="localMatch.autoStreaming" md3 sm6 xs12 px-2>
              <v-menu
                ref="menuAutoStreaming"
                v-model="menuAutoStreaming"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                max-width="290px"
                min-width="290px"
              >
                <template #activator="{ on }">
                  <v-text-field
                    ref="timeAutoStreaming"
                    v-model="localMatch.timeAutoStreaming"
                    prepend-icon="access_time"
                    readonly
                    :label="$tc('fields.matches.dateAutoStreaming')"
                    v-on="on"
                  />
                </template>
                <v-time-picker
                  v-if="menuAutoStreaming"
                  v-model="localMatch.timeAutoStreaming"
                  full-width
                  :readonly="localMatch.streamingStarted"
                  @click:minute="
                    $refs.menuAutoStreaming.save(localMatch.timeAutoStreaming)
                  "
                />
              </v-menu>
            </v-flex>
          </template>
          <v-flex v-if="localMatch._id && localMatch.broadcastId" md3 xs12 px-2>
            <v-text-field
              v-model="localMatch.broadcastId"
              readonly
              :label="$tc('fields.matches.broadcastId')"
            />
          </v-flex>
          <v-flex md2 xs6 px-2>
            <v-switch
              v-model="localMatch.active"
              :label="$t('fields.global.active')"
              color="primary"
            />
            <v-switch
              v-model="localMatch.isVisible"
              :label="$t('fields.global.visible')"
              color="primary"
            />
          </v-flex>
          <v-flex v-if="isAdmin" md3 xs6 px-2>
            <v-switch
              v-model="localMatch.emissionStatus"
              :disabled="localMatch.publicSale"
              :label="$t('fields.matches.emissionStatus')"
              color="primary"
            />
          </v-flex>
          <v-flex md3 xs6 px-2>
            <v-switch
              v-model="localMatch.publicSale"
              :disabled="localMatch.emissionStatus"
              :label="$t('fields.matches.publicSale')"
              color="primary"
            />
          </v-flex>
          <v-flex v-if="localMatch._id" xs12>
            <v-layout row wrap>
              <v-flex
                v-if="!localMatch.started && !localMatch.ended"
                md3
                xs6
                px-2
              >
                <button-confirm
                  color="primary"
                  :text="$t('action.buttons.startMatch')"
                  :disabled="loading"
                  @submit="startMatch(localMatch._id)"
                />
              </v-flex>
              <v-flex
                v-else-if="localMatch.started && !localMatch.ended"
                md3
                xs6
                px-2
              >
                <button-confirm
                  color="error"
                  :text="$t('action.buttons.endMatch')"
                  :disabled="loading"
                  @submit="endMatch(localMatch._id)"
                />
              </v-flex>
              <template
                v-if="selected.streamingAndRecording || selected.recording"
              >
                <v-flex
                  v-if="
                    !!selected.location &&
                    !localMatch.streamingStarted &&
                    !localMatch.ended
                  "
                  md3
                  xs6
                  px-2
                >
                  <button-confirm
                    color="primary"
                    :text="$t('action.buttons.startStreaming')"
                    :disabled="loading"
                    @submit="
                      ifStartStreaming(localMatch._id, localMatch.location)
                    "
                  />
                </v-flex>
                <v-flex
                  v-else-if="
                    localMatch.streamingStarted &&
                    !localMatch.streamingEnded &&
                    localMatch.ended
                  "
                  md3
                  xs6
                  px-2
                >
                  <button-confirm
                    color="error"
                    :text="$t('action.buttons.endStreaming')"
                    :disabled="loading"
                    @submit="endStreaming(localMatch._id)"
                  />
                </v-flex>
              </template>
              <v-flex
                v-if="localMatch._id && localMatch.ended && !isStreaming"
                md2
                xs6
                px-2
              >
                <v-btn
                  class="blue"
                  dark
                  small
                  block
                  @click="dialogUpload = true"
                >
                  {{
                    $t('action.actions.upload', {
                      field: $t('fields.matches.video'),
                    })
                  }}
                </v-btn>
              </v-flex>
              <v-flex
                v-if="
                  can('download', 'match') && video && localMatch.isDownloadable
                "
                md2
                xs6
                px-2
              >
                <v-btn
                  small
                  block
                  color="warning"
                  @click="dialogDownload = true"
                >
                  {{ $t('fields.global.downloadMatch') }}
                </v-btn>
              </v-flex>
              <v-flex
                v-if="
                  localMatch._id &&
                  localMatch.recoveryStatus &&
                  localMatch.recoveryStatus != 'ready'
                "
                md2
                xs6
                px-2
              >
                <v-btn
                  class="blue"
                  dark
                  small
                  block
                  :loading="loadingRecovery"
                  @click="_recoveryReady"
                >
                  {{ $t('titles.matches.recoveryReady') }}
                </v-btn>
              </v-flex>
              <!-- v-if="localMatch._id && localMatch.ended && !isStreaming" -->
              <v-flex v-if="showRecoveryVideo" md2 xs6 px-2>
                <dialog-recovery-video
                  v-model="dialogUpload"
                  :object-id="localMatch._id"
                  :date="localMatch.date"
                  :time="localMatch.time"
                  :duration="matchesDuration"
                  :camera="selected.location.name"
                  :stadium-id="selected.location.stadiumId"
                  :location="selected.location._id"
                  :video-exist="video"
                />
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
    <dialog-confirm-streaming
      ref="confirmStreaming"
      :text="confirmStreamingMessage"
      @submit="startStreaming(localMatch._id)"
    />
    <dialog-time-difference
      ref="timeDifference"
      :time="localMatch.time"
      :time-auto-streaming="localMatch.timeAutoStreaming"
      @cancel="onChangeTime"
    />
    <dialog-upload-video
      v-model="dialogUpload"
      :object-id="localMatch._id"
      :video-exist="video"
      type-video="match"
      @upload="onUploadVideo"
    />
    <dialog-new-team
      :dialog="createTeamDialog"
      :predata="predata"
      @close="createTeamDialog = false"
      @createdTeam="fetchTeam"
    />
    <dialog-download-match
      :id="localMatch._id"
      :dialog="dialogDownload"
      :video="video"
      @close="dialogDownload = false"
    />
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapState, mapActions } from 'vuex'
import CategoryMenu from '@/components/management/categories/CategoryMenu'
import DatePicker from '@/components/widgets/Input/DatePicker'
import EntityField from '@/components/widgets/Input/EntityField'
import PitchField from '@/components/widgets/Input/PitchField'
import TeamField from '@/components/widgets/Input/TeamField'
import TournamentField from '@/components/widgets/Input/TournamentField'
import ButtonConfirm from '@/components/widgets/button/ButtonConfirm'
import DialogConfirm from '@/components/widgets/dialog/DialogConfirm'
import DialogDownloadMatch from '@/components/widgets/dialog/DialogDownloadMatch'
import DialogNewTeam from '@/components/widgets/dialog/DialogNewTeam'
import DialogRecoveryVideo from '@/components/widgets/dialog/DialogRecoveryVideo.vue'
import DialogTimeDifference from '@/components/widgets/dialog/DialogTimeDifference'
import DialogUploadVideo from '@/components/widgets/dialog/DialogUploadVideo'
import { apiAxios, formatDate, formatDatePicker } from '@/helpers'

export default {
  name: 'TabInformation',
  components: {
    DialogUploadVideo,
    DialogRecoveryVideo,
    CategoryMenu,
    TeamField,
    TournamentField,
    PitchField,
    DatePicker,
    ButtonConfirm,
    DialogConfirmStreaming: DialogConfirm,
    DialogTimeDifference,
    DialogNewTeam,
    DialogDownloadMatch,
    EntityField,
  },
  props: {
    match: {
      type: Object,
      required: true,
      default: () => {},
    },
    tournamentView: {
      type: Boolean,
      required: false,
      default: false,
    },
    tournament: {
      type: Object,
      required: false,
      default: null,
    },
    phase: {
      type: Object,
      required: false,
      default: null,
    },
    group: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      menuTime: false,
      menuAutoStreaming: false,
      confirmStreamingMessage: '',
      streamingType: 'streamingAndRecording',
      matchesDuration: 90,
      dialogUpload: false,
      dialogDownload: false,
      createTeamDialog: false,
      auxHomeCategoryId: '',
      typeTeam: '',
      predata: {},
      names: [],
    }
  },
  watch: {
    streamingType(val) {
      this.localMatch.publicSale = true
      this.localMatch.emissionStatus = false
      this.localMatch.isVisible = true
      this.localMatch.active = true
      if (val === 'recording') {
        this.localMatch.recording = true
        this.localMatch.noRecord = false
        this.localMatch.streamingAndRecording = false
      } else if (val === 'streamingAndRecording') {
        this.localMatch.recording = false
        this.localMatch.noRecord = false
        this.localMatch.streamingAndRecording = true
      } else if (val === 'noRecord') {
        this.localMatch.noRecord = true
        this.localMatch.recording = false
        this.localMatch.streamingAndRecording = false
      }
    },
    'localMatch.recording': {
      handler: 'setStreamingType',
      immediate: true,
      deep: true,
    },
    'localMatch.noRecord': {
      handler: 'setStreamingType',
      immediate: true,
      deep: true,
    },
    'localMatch.type': {
      handler: function (val) {
        if (val === 'league') {
          this.localMatch.categoryIdVisitor = this.localMatch.categoryId
        }
      },
    },
    'localMatch.categoryId': {
      handler: function (val) {
        if (this.localMatch.type === 'league') {
          this.localMatch.categoryIdVisitor = val
        }
      },
    },
    'localMatch.homeTeamId': {
      handler: function (val, oldVal) {
        if (oldVal) {
          this.localMatch.categoryId = null
          this.localMatch.categoryIdVisitor = null
        }
        if (
          val &&
          !this.localMatch.categoryId &&
          this.localMatch.type !== 'tournament'
        ) {
          this.$store.dispatch('teams/getOne', val).then((team) => {
            if (team.categoryId) {
              this.auxHomeCategoryId =
                team.categoryId.parentCategory.parentCategory._id ||
                team.categoryId.parentCategory.parentCategory.id
              this.localMatch.categoryId =
                team.categoryId._id || team.categoryId.id
            }
          })
        }
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    ...mapState({
      selected: (state) => state.matches.selected,
      loading: (state) => state.matches.loading,
      video: (state) => state.matches.video,
    }),
    ...mapGetters({
      matchTypes: 'matches/matchTypes',
      teamSelectList: 'teams/selectList',
      tournamentSelectList: 'tournaments/selectList',
      pitchSelectList: 'pitches/selectList',
    }),
    localMatch: {
      get() {
        return this.match
      },
      set(value) {
        this.$emit('update:match', value)
      },
    },
    dateAutoStreaming() {
      return formatDate(this.localMatch.dateAutoStreaming)
    },
    _matchTypes() {
      if (this.tournament) {
        return this.matchTypes.filter((type) => type.value == 'tournament')
      } else if (this.isAdmin || this.match._id) {
        return this.matchTypes
      } else if (this.is('coordinator') || this.is('trainer')) {
        return this.matchTypes.filter((type) => type.value != 'tournament')
      } else {
        return this.matchTypes.filter((type) => type.value == 'friendly')
      }
    },
    phaseHours() {
      if (!this.phase || !this.phase.hours || !this.phase.hours.length) {
        return null
      }

      return this.phase.hours.map((hour) => ({ value: hour, text: hour }))
    },
    isStreaming() {
      return this.localMatch.streamingStarted && !this.localMatch.streamingEnded
    },
    isSuperUser() {
      return this.authUser.roles.some(
        (role) =>
          role.confirm &&
          role.roleId &&
          role.roleId.id === '62e829b6868951330cfdab48'
      )
    },
    showRecoveryVideo() {
      if (
        !this.selected._id ||
        !this.selected.time ||
        !this.selected.date ||
        !this.selected.location ||
        !this.selected.location.cameraId
      )
        return false

      const [hour, minute] = this.selected.time.split(':')

      const endDate = moment(this.selected.date.split('T')[0])
        .set({
          hour,
          minute,
          second: 0,
        })
        .add(this.matchesDuration, 'minutes')

      console.log('this.elected.date :>> ', this.selected.date)

      console.log('showRecoveryVideo now :>> ', moment())
      console.log('showRecoveryVideo endDate :>> ', endDate)

      console.log('showRecoveryVideo', moment().diff(endDate, 'minutes'))

      return moment().diff(endDate, 'minutes') > 0
    },
  },
  methods: {
    ...mapActions({
      endMatch: 'matches/endMatch',
      startMatch: 'matches/startMatch',
      startStreaming: 'matches/startStreaming',
      endStreaming: 'matches/endStreaming',
      recoveryReady: 'matches/recoveryReady',
      isCameraInUse: 'pitches/isCameraInUse',
    }),
    async fetchNames() {
      await apiAxios
        .post('/matches', { type: 'friendly', isName: true })
        .then((response) => {
          this.names = response.data.data.match =
            response.data.data.match.filter(
              (thing, index, self) =>
                index ===
                self.findIndex(
                  (t) => t.place === thing.place && t.name === thing.name
                )
            )
        })
    },
    clear() {
      this.$validator.pause()
      this.nextTick(() => {
        this.$validator.reset()
        this.$validator.resume()
      })
      this.streamingType = 'streamingAndRecording'
    },
    onChangeType(type) {
      /* Revisar porque se manda a settear categoryId en blanco */
      if (type === 'tournament') {
        this.localMatch.categoryId = ''
        this.localMatch.categoryIdVisitor = ''
      }
      this.localMatch.tournamentId = ''
    },
    onChangeTime(time) {
      if (time && this.localMatch.autoStreaming) {
        let timeFormat = moment(time, 'HH:mm').subtract(5, 'minutes')
        this.$nextTick(() => {
          this.localMatch.timeAutoStreaming = timeFormat.format('HH:mm')
          if (this.$refs.timeAutoStreaming) {
            this.$refs.timeAutoStreaming.lazyValue = timeFormat.format('HH:mm')
          }
        })
      }
    },
    onChangeAutoStreaming(autoStreaming) {
      if (autoStreaming) {
        this.$nextTick(() => {
          this.onChangeTime(this.localMatch.time)
        })
      } else {
        this.localMatch.dateAutoStreaming = null
        this.localMatch.timeAutoStreaming = null
      }
    },
    formatDatePicker(date) {
      return formatDatePicker(date)
    },
    onUploadVideo() {
      this.$store.dispatch('matches/fetchOne', this.localMatch._id)
    },
    setStreamingType() {
      if (this.localMatch.recording) {
        this.streamingType = 'recording'
      } else if (this.localMatch.noRecord) {
        this.streamingType = 'noRecord'
      } else {
        this.streamingType = 'streamingAndRecording'
        this.localMatch.streamingAndRecording = true
      }
    },
    ifStartStreaming(matchId, pitchId) {
      this.isCameraInUse(pitchId)
        .then(({ status, inUse }) => {
          this.confirmStreamingMessage = ''
          if (status === false) {
            this.confirmStreamingMessage =
              this.$t('titles.global.cameraOffline') + ' '
          } else if (inUse) {
            this.confirmStreamingMessage =
              this.$t('titles.global.cameraInUse') + ' '
          }
          if (this.confirmStreamingMessage) {
            this.confirmStreamingMessage += this.$t(
              'titles.global.confirmMessage',
              {
                field:
                  `<span class="text-uppercase primary--text">` +
                  this.$t('action.buttons.startStreaming') +
                  '</span>',
              }
            )
            this.$refs.confirmStreaming.show()
          } else {
            this.startStreaming(matchId)
          }
        })
        .catch(() => {
          this.startStreaming(matchId)
        })
    },
    async _recoveryReady() {
      try {
        this.loadingRecovery = true
        await this.recoveryReady(this.localMatch._id)
        this.localMatch.recoveryStatus = 'ready'
      } finally {
        this.loadingRecovery = false
      }
    },
    openNewTeamModal(type) {
      this.typeTeam = type
      if (type === 'home') {
        this.predata.entityId = this.localMatch.homeEntityId
        this.predata.categoryId = ''
      } else {
        this.predata.entityId = this.localMatch.visitorEntityId
        if (this.localMatch.type === 'league')
          this.predata.categoryId = this.localMatch.categoryId
      }
      this.createTeamDialog = true
    },
    fetchTeam(value) {
      if (this.typeTeam === 'home') {
        this.localMatch.homeTeamId = value._id
        this.localMatch.categoryId = value.categoryId[0]
        this.localMatch.homeEntityId = value.entityId
      } else {
        this.localMatch.visitorTeamId = value._id
        this.localMatch.categoryIdVisitor = value.categoryId[0]
        this.localMatch.visitorEntityId = value.entityId
      }
    },
    setName(name) {
      this.$set(this.localMatch, 'name', name.name)
    },
  },
  mounted() {
    this.fetchNames()
  },
}
</script>

<style>
.v-chip .v-chip__content {
  cursor: pointer;
}
</style>
